import { baseURL } from './baseUrl';

// Legacy search endpoint (kept for backwards compatibility).
export const apiSearch = async (searchTerm: string) => {
    const response = await baseURL.get(`search?query=${searchTerm}`, {
        headers: {
            'Content-Type': 'application/json',
        },
    });
    return response.data;
};

// Site-wide search (mirrors trade). Posts to /search-site and normalises the
// response into the shape the SearchModal consumes:
//   - filters.application / range / brand : CMS page names for direct navigation
//   - filters.design                      : raw design objects { name: { range, item_group, ... } }
//   - filters.colour / design_style       : plain string arrays
//   - collectionDetails                   : full CMS page objects (item_group lookup)
//   - rangeItemGroups                     : range name -> item_group (from design data)
// The /search-site endpoint uses the same auth guard as products/multi_filter,
// which consumer already calls unauthenticated, so no token is required here.
export const apiFilterSearch = async (searchTerm: string, _currentFilters?: any) => {
    const response = await baseURL.post(
        '/search-site',
        {
            query: searchTerm,
            only_published: true,
        },
        {
            headers: {
                'Content-Type': 'application/json',
            },
        }
    );

    const data = response.data;

    // Helper function to get unique values from arrays of objects.
    const getUniqueValues = (items: any[], field: string): string[] => {
        if (!items || !Array.isArray(items)) return [];
        const values = items
            .map(item => item[field])
            .filter(val => val && val.trim() !== '' && val !== '-');
        return Array.from(new Set(values));
    };

    // Helper function to get values directly from an array of strings.
    const getDirectValues = (items: any[]): string[] => {
        if (!items || !Array.isArray(items)) return [];
        return items.filter(val => val && val.trim() !== '' && val !== '-');
    };

    // Create a map of range name -> item_group from design data, so collections
    // that only surface through a design match can still navigate directly.
    const rangeItemGroupMap: Record<string, string> = {};
    if (data.results?.filters?.design && Array.isArray(data.results.filters.design)) {
        data.results.filters.design.forEach((designObj: any) => {
            const designName = Object.keys(designObj)[0];
            if (designName && designObj[designName]) {
                const { range, item_group } = designObj[designName];
                if (range && item_group && !rangeItemGroupMap[range]) {
                    rangeItemGroupMap[range] = item_group;
                }
            }
        });
    }

    return {
        filters: {
            // CMS Pages - for direct navigation
            application: getUniqueValues(data.results?.pages?.application || [], 'category_name'),
            range: getUniqueValues(data.results?.pages?.collection || [], 'category_name'),
            brand: getUniqueValues(data.results?.pages?.brand || [], 'category_name'),

            // Product filters
            design: data.results?.filters?.design || [],
            colour: getDirectValues(data.results?.filters?.colour || []),
            design_style: getDirectValues(data.results?.filters?.design_style || []),
        },
        products: data.results?.products || {},
        // Preserve the full page objects for item_group lookup.
        collectionDetails: {
            application: data.results?.pages?.application || [],
            collection: data.results?.pages?.collection || [],
            brand: data.results?.pages?.brand || [],
        },
        // Map of range names to their item_groups (extracted from design data).
        rangeItemGroups: rangeItemGroupMap,
    };
};
